/**
 * O cliente da API do Nosso Sempre.
 *
 * Cada método aqui corresponde a uma linha do mapa `ROUTES` em
 * `server/src/index.js`. Se não está lá, não entra aqui: um método que aponta
 * para rota inventada compila, passa no tipo e só falha na mão de quem usa.
 *
 * Sessão é cookie (`ns_sess`), nunca cabeçalho: `sessionSlug` no servidor lê
 * o `Cookie` e mais nada. No navegador o `credentials: 'include'` resolve. Fora
 * dele — Electron a partir do processo principal, React Native — o fetch pode
 * não carregar o cookie sozinho, então quem constrói passa `lerCookie`.
 */

import {
  eventosDeCalendarioDeMapa,
  listasDeCalendariosDeMapa,
  type EventoDeCalendario,
  type ListasDeCalendarios,
} from '../agenda/calendarios';
import {
  POR_PAGINA,
  diaIso,
  extratoDeJson,
  saldoDeJson,
  type Extrato,
  type Saldo,
} from '../financas/conta';
import { idioma, traduzir } from '../i18n/idioma';
import { enderecoDeFora } from '../nucleo/endereco';
import type { AuthStatus, LoginResponse } from '../types/auth';
import type { Casa } from '../types/casa';
import type { Documento, Evento, Pagina, QuadroDoFluxo } from '../types/dados';
import { entradaIptv, episodiosIptv, itensIptv, listasIptv } from './iptv';
import type { EntradaIptv, FluxoIptv, LigacaoIptv, ListaIptv, PareamentoIptv } from './iptv';

export interface APIConfig {
  /**
   * Onde a API mora. O padrão serve o caso normal: o Caddy publica app e API
   * na mesma origem, então caminho relativo basta e não há CORS.
   */
  baseUrl?: string;
  /** Milissegundos. Todo pedido tem prazo; nenhum fica pendurado para sempre. */
  timeout?: number;
  /** Plataforma sem cookie automático devolve `ns_sess=<token>` aqui. */
  lerCookie?: () => string | null | undefined;
  /** Chamado quando o servidor responde 401, para a tela voltar à porta. */
  aoExpirar?: () => void;
}

/** Erro com o status junto: a tela decide entre "senha errada" e "sem rede". */
export class ErroAPI extends Error {
  readonly status: number;
  readonly retryAfter?: number;

  constructor(status: number, message: string, retryAfter?: number) {
    super(message);
    this.name = 'ErroAPI';
    this.status = status;
    this.retryAfter = retryAfter;
  }
}

const TEMPO_PADRAO = 15_000;

/**
 * Só a espera pelo começo da resposta do Kovu, e não pela resposta inteira --
 * mas só onde o `fetch` resolve no cabeçalho. Veja `emPedacos()` abaixo.
 *
 * A conversa em pedaços não pode ter prazo: ela leva o tempo que o modelo levar,
 * e cortar no meio seria cortar a frase. Mas o `fetch` antes dela pode -- e
 * precisa: um servidor que aceita a conexão e nunca manda cabeçalho nenhum
 * deixava o "o Kovu está escrevendo" girando para sempre, sem erro e sem fim, e
 * o único jeito de sair era fechar a tela.
 *
 * Folgado de propósito. O `/ai` responde com o cabeçalho antes de falar com o
 * modelo, então trinta segundos é tempo de sobra para o que é quase imediato;
 * ele está aqui para trocar "para sempre" por "um limite", não para apertar.
 */
const PRAZO_CABECALHO = 30_000;

/**
 * O mesmo prazo onde ele acaba valendo pela resposta inteira.
 *
 * No React Native o `fetch` é o `whatwg-fetch` por cima de `XMLHttpRequest`: a
 * promessa só resolve no `onload`, ou seja, depois do corpo todo. Ali os trinta
 * segundos acima deixavam de ser a espera pelo cabeçalho e viravam o teto da
 * resposta inteira, rodadas de ferramenta incluídas -- e uma pergunta que manda
 * o Kovu abrir a cozinha e a lista de compras passa disso com folga. O
 * `controle.abort()` corta o pedido, e no lugar da resposta vinha "o servidor
 * demorou demais". Era esse o Kovu que, no mercado, não respondeu.
 *
 * Mesmo teto do servidor por chamada (`TIMEOUT_MS` em `server/src/ai.js`): o
 * limite continua existindo, só que agora do tamanho certo.
 */
const PRAZO_RESPOSTA = 120_000;

/**
 * Se dá para ler o corpo enquanto ele chega. Falso no React Native, onde o
 * `Response` não tem `body` nenhum -- e é justamente lá que o prazo precisa ser
 * o da resposta inteira. Perguntado na hora, e não uma vez ao carregar: se um
 * dia o aparelho passar a ler em pedaços, a conta se acerta sozinha.
 */
const emPedacos = () => typeof Response !== 'undefined' && 'body' in Response.prototype;

/**
 * O corpo que o servidor manda quando dá errado: `json({message}, {status})`.
 *
 * `error` está aqui porque `/recipes/import` responde 400 com esse nome, e é o
 * único campo que traz o motivo em português ("esse site não publica a receita
 * em formato legível"). Sem lê-lo, a tela mostraria só "Erro 400".
 */
interface CorpoDeErro {
  message?: string;
  error?: string;
  retryAfter?: number;
}

/** `json()` devolve `unknown`; isto olha antes de tratar como o erro do servidor. */
const comoErro = (corpo: unknown): CorpoDeErro =>
  corpo && typeof corpo === 'object' ? (corpo as CorpoDeErro) : {};

/**
 * O motivo, na língua do app.
 *
 * O servidor fala português: é a língua em que as mensagens dele estão
 * escritas, e mudar isso pediria um protocolo novo só para o texto de erro. O
 * `traduzir` devolve a própria frase quando não conhece o verbete, então uma
 * mensagem ainda não traduzida sai exatamente como sempre saiu — o pior caso
 * daqui é o de antes, nunca um `Erro 500` no lugar do motivo.
 */
const recadoDoErro = (corpo: unknown, status: number): string => {
  const recado = comoErro(corpo).message ?? comoErro(corpo).error;
  return recado ? traduzir(recado) : traduzir('Erro {status}.', { status });
};

/** O que `/recipes/import` devolve: uma receita ainda sem id, para revisar. */
export interface ReceitaImportada {
  titulo: string;
  descricao: string;
  ingredientes: Array<{ nome: string; quantidade: number; unidade: string }>;
  passos: string[];
  porcoes: number;
  minutos: number;
  imagemUrl: string;
}

/** O que `/books/text` devolve: o livro em texto puro, e de onde ele veio. */
export interface TextoDeLivro {
  text: string;
  /** O endereço que respondeu. Vazio quando nenhum respondeu. */
  usedUrl: string;
  /** Os endereços tentados, na ordem — o leitor mostra isso no erro. */
  attempted: string[];
  error?: string;
}

/** Um item de uma lista da Alexa, como o servidor normaliza. */
export interface ItemAlexa {
  id: string;
  text: string;
  done: boolean;
}

/** Uma lista da Alexa, como ela está na Amazon ou na cópia compartilhada. */
export interface ListaAlexa {
  id: string;
  name: string;
  type?: string;
  items?: ItemAlexa[];
  importedBy?: string;
  importedAt?: string;
}

/**
 * Se a sessão da Amazon está de pé. O cookie nunca faz parte disto: fica no
 * servidor e não volta para ninguém, nem para quem colou.
 */
export interface AlexaStatus {
  connected: boolean;
  host: string;
  account: { name?: string; email?: string } | null;
  /** Uma colagem sem `csrf` lê tudo e falha calada na escrita. */
  canWrite: boolean;
  connectedAt?: string | null;
  checkedAt?: string | null;
  lastError?: string | null;
  /** O que impede as listas, com a sessão de pé. Reação diferente de `lastError`. */
  listsError?: string | null;
}

/**
 * Um servidor de gelo (STUN/TURN) para a chamada de voz da sessão de cinema.
 *
 * É de propósito um objeto solto, e não o `RTCIceServer` do navegador: este
 * pacote também roda onde não há DOM, e uma tipagem de WebRTC aqui obrigaria
 * todo mundo a carregar as `lib` do navegador para chamar `entrar()`.
 */
export interface ServidorDeGelo {
  urls: string | string[];
  username?: string;
  credential?: string;
  /** Quanto tempo a credencial vale, em segundos. */
  ttl?: number;
}

/**
 * Uma chave de acesso, como o servidor a descreve.
 *
 * Não há material de chave nenhum aqui de propósito: rótulo, o endereço onde
 * ela vale e as duas datas. É tudo o que a tela precisa mostrar.
 */
export interface Chave {
  id: string;
  rotulo: string;
  /** O endereço onde a chave foi feita. Uma do site não abre o de reserva. */
  endereco: string;
  criadaEm: Date;
  usadaEm: Date | null;
}

const comoChave = (bruto: unknown): Chave => {
  const j = (bruto ?? {}) as Record<string, unknown>;
  const ms = (v: unknown): number => (typeof v === 'number' ? v : 0);
  return {
    id: String(j.id ?? ''),
    rotulo: (j.label as string) || 'Chave sem nome',
    endereco: (j.rpId as string) ?? '',
    criadaEm: new Date(ms(j.createdAt)),
    usadaEm: j.lastUsedAt == null ? null : new Date(ms(j.lastUsedAt)),
  };
};

/**
 * Uma conta ligada por cookies, como o servidor a devolve em `/connections`.
 *
 * Os cookies ficam lá: daqui só vem quantos são e como se chamam. A tela
 * mostra o nome, nunca o valor — é a promessa que o passo a passo faz.
 */
export interface Conexao {
  id: string;
  nome: string;
  host: string;
  estado: string;
  detalhe: string;
  aviso: string;
  cookies: number;
  nomes: string[];
  conectadaEm: Date | null;
  checadaEm: Date | null;
  por: string;
  delegada: boolean;
  dono: string;
  donoNome: string;
  minha: boolean;
}

/** Um site já conhecido, com host e a página que só quem entrou carrega. */
export interface PresetDeConexao {
  id: string;
  nome: string;
  host: string;
  caminho: string;
  /** O cookie de login daquele site. Vazio quando não sabemos qual é. */
  marca: string;
}

const comoConexao = (bruto: unknown): Conexao => {
  const j = (bruto ?? {}) as Record<string, unknown>;
  const data = (v: unknown): Date | null => {
    if (typeof v !== 'string' || !v) return null;
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? null : d;
  };
  return {
    id: String(j.id ?? ''),
    nome: (j.nome as string) ?? '',
    host: (j.host as string) ?? '',
    estado: (j.estado as string) || 'desligada',
    detalhe: (j.detalhe as string) ?? '',
    aviso: (j.aviso as string) ?? '',
    cookies: typeof j.cookies === 'number' ? j.cookies : 0,
    nomes: Array.isArray(j.nomes) ? j.nomes.map(String) : [],
    conectadaEm: data(j.conectadaEm),
    checadaEm: data(j.checadaEm),
    por: (j.por as string) ?? '',
    delegada: j.delegada === true,
    dono: (j.dono as string) ?? '',
    donoNome: (j.donoNome as string) ?? '',
    minha: j.minha === true,
  };
};

export class NossoSempreAPI {
  private readonly baseUrl: string;
  private readonly timeout: number;
  private readonly lerCookie?: () => string | null | undefined;
  private readonly aoExpirar?: () => void;

  constructor(config: APIConfig = {}) {
    this.baseUrl = (config.baseUrl ?? '/api').replace(/\/$/, '');
    this.timeout = config.timeout ?? TEMPO_PADRAO;
    this.lerCookie = config.lerCookie;
    this.aoExpirar = config.aoExpirar;
  }

  /** A URL de uma rota, para quem precisa dela crua (EventSource, vídeo). */
  url(caminho: string, query?: Record<string, string | number | undefined>): string {
    const base = `${this.baseUrl}${caminho}`;
    if (!query) return base;
    const p = new URLSearchParams();
    for (const [k, v] of Object.entries(query)) {
      if (v !== undefined) p.set(k, String(v));
    }
    const s = p.toString();
    return s ? `${base}?${s}` : base;
  }

  private async pedir<T>(
    caminho: string,
    init: RequestInit & {
      query?: Record<string, string | number | undefined>;
      /** Prazo só deste pedido, para o que sabidamente demora mais. */
      prazo?: number;
      /**
       * Não derruba a sessão num 401.
       *
       * As rotas que conferem a senha de novo — trocar o código, mexer nas
       * chaves — respondem 401 quando a senha digitada está errada. Sem isto,
       * um dígito trocado expulsaria a pessoa do app inteiro em vez de dizer
       * "não confere".
       */
      manterSessao?: boolean;
    } = {},
  ): Promise<T> {
    const { query, prazo: prazoPedido, manterSessao, ...resto } = init;
    const controle = new AbortController();
    const prazo = setTimeout(() => controle.abort(), prazoPedido ?? this.timeout);

    const cabecalhos: Record<string, string> = {
      Accept: 'application/json',
      ...((resto.headers as Record<string, string>) ?? {}),
    };
    // JSON é o que quase toda rota manda, mas não todas: a foto sobe crua, com
    // o tipo dela. Quem já disse o próprio `Content-Type` manda nele.
    if (resto.body !== undefined && !cabecalhos['Content-Type']) {
      cabecalhos['Content-Type'] = 'application/json';
    }
    const cookie = this.lerCookie?.();
    if (cookie) cabecalhos.Cookie = cookie;

    let resposta: Response;
    try {
      resposta = await fetch(this.url(caminho, query), {
        ...resto,
        headers: cabecalhos,
        credentials: 'include',
        signal: controle.signal,
      });
    } catch (err) {
      // `abort` e queda de rede chegam iguais aqui; separar ajuda a tela.
      if ((err as Error)?.name === 'AbortError') {
        throw new ErroAPI(0, traduzir('O servidor demorou demais para responder.'));
      }
      throw new ErroAPI(0, traduzir('Não foi possível falar com o servidor.'));
    } finally {
      clearTimeout(prazo);
    }

    if (resposta.status === 204) return undefined as T;

    const corpo = await resposta.json().catch(() => null);
    if (!resposta.ok) {
      if (resposta.status === 401 && !manterSessao) this.aoExpirar?.();
      const e = comoErro(corpo);
      throw new ErroAPI(resposta.status, recadoDoErro(corpo, resposta.status), e.retryAfter);
    }
    return corpo as T;
  }

  // ── Porta de entrada ───────────────────────────────────────────────

  /** Responde sem sessão: a tela de entrada precisa dos nomes antes do login. */
  status(): Promise<AuthStatus> {
    return this.pedir<AuthStatus>('/auth/status');
  }

  /**
   * Entra com a senha de quatro dígitos. Não há campo de usuário: o servidor
   * descobre quem é resolvendo a senha. 401 é senha errada, 429 é bloqueio
   * temporário e traz `retryAfter`.
   */
  entrar(passcode: string): Promise<LoginResponse> {
    return this.pedir<LoginResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ passcode }),
    });
  }

  /**
   * Cadastra a senha de um slug que ainda não tem, e já entra: o servidor
   * devolve o mesmo corpo do login e manda a sessão no `Set-Cookie`.
   *
   * `nome` só vale numa casa que ninguém configurou ainda — é lá que quem
   * chegou escolhe como quer aparecer. Vazio deixa o nome que já estava.
   */
  cadastrar(slug: string, passcode: string, nome?: string): Promise<LoginResponse> {
    return this.pedir<LoginResponse>('/auth/enroll', {
      method: 'POST',
      body: JSON.stringify({ slug, passcode, nome }),
    });
  }

  // ── As chaves da casa ──────────────────────────────────────────────
  //
  // As duas maneiras de uma casa começar: abrindo uma, ou entrando na que a
  // outra pessoa já abriu. Nenhuma das duas entra em conta nenhuma — só dizem
  // de que casa é a porta que vem a seguir, gravando o cookie `ns_casa`.

  /** Abre uma casa nova. O código é o que a outra pessoa digita no aparelho dela. */
  abrirCasa(nome?: string): Promise<{ casa: string; codigo: string }> {
    return this.pedir('/casas', {
      method: 'POST',
      body: JSON.stringify({ nome }),
    });
  }

  /** Aponta este aparelho para uma casa que já existe. 404 é código errado. */
  entrarNaCasa(codigo: string): Promise<{ casa: string }> {
    return this.pedir('/casas/entrar', {
      method: 'POST',
      body: JSON.stringify({ codigo }),
    });
  }

  /**
   * Troca a senha. O slug vai no corpo porque o servidor confere a senha
   * atual contra ele — `handlePasscodeChange` lê `{slug, currentPasscode,
   * newPasscode}` e recusa com 400 qualquer corpo sem os três.
   *
   * `manterSessao` porque senha errada aqui é 401, e um dígito trocado não
   * pode expulsar ninguém do app.
   */
  trocarSenha(slug: string, atual: string, nova: string): Promise<{ ok: true }> {
    return this.pedir('/auth/passcode/change', {
      method: 'POST',
      manterSessao: true,
      body: JSON.stringify({ slug, currentPasscode: atual, newPasscode: nova }),
    });
  }

  /**
   * Troca a senha sem saber a antiga: quem está do lado digita a dela.
   *
   * `parceiro` só é preciso numa casa de mais de duas pessoas — com uma só do
   * outro lado o servidor descobre sozinho. Quando não descobre, responde 400
   * com `opcoes`, e é a tela que pergunta de quem é o código.
   */
  recuperarSenha(
    slug: string,
    nova: string,
    senhaDoParceiro: string,
    parceiro?: string,
  ): Promise<{ ok: true }> {
    return this.pedir('/auth/reset', {
      method: 'POST',
      manterSessao: true,
      body: JSON.stringify({
        slug,
        newPasscode: nova,
        partnerPasscode: senhaDoParceiro,
        partnerSlug: parceiro,
      }),
    });
  }

  // ── Chaves de acesso ───────────────────────────────────────────────
  //
  // Nada de material de chave chega aqui: o servidor guarda a chave pública e
  // devolve só rótulo, endereço e datas. O `rpId` aparece na tela porque uma
  // chave feita no endereço principal não abre o de reserva.
  //
  // Todas pedem a senha de novo, de propósito: um aparelho destravado em cima
  // da mesa não pode bastar para cadastrar mais uma chave.

  async chaves(slug: string, senha: string): Promise<Chave[]> {
    const corpo = await this.pedir<{ passkeys?: unknown[] }>('/auth/passkey/list', {
      method: 'POST',
      manterSessao: true,
      body: JSON.stringify({ slug, passcode: senha }),
    });
    return (corpo.passkeys ?? []).map(comoChave);
  }

  apagarChave(slug: string, senha: string, id: string): Promise<{ ok: true }> {
    return this.pedir('/auth/passkey/delete', {
      method: 'POST',
      manterSessao: true,
      body: JSON.stringify({ slug, passcode: senha, id }),
    });
  }

  renomearChave(slug: string, senha: string, id: string, rotulo: string): Promise<{ ok: true }> {
    return this.pedir('/auth/passkey/rename', {
      method: 'POST',
      manterSessao: true,
      body: JSON.stringify({ slug, passcode: senha, id, label: rotulo }),
    });
  }

  /**
   * O desafio que o aparelho precisa para criar uma chave. Vai cru para lá.
   *
   * O embrulho `publicKey` sai aqui, e é uma divergência do Flutter de
   * propósito. O servidor responde `{ publicKey: {...} }` — o formato de quem
   * já vai chamar `navigator.credentials.create` — e lá o
   * `web/webauthn_bridge.js` embrulhava o corpo inteiro outra vez
   * (`publicKey: decodeOptions(JSON.parse(optionsJson))`), de modo que o
   * desafio chegava um nível fundo demais e o navegador recusava por falta de
   * `challenge`. Criar chave nunca funcionou lá, e o porte trouxe o defeito
   * junto. Desembrulhar num lugar só conserta os dois lados.
   */
  async opcoesDeCadastro(slug: string, senha: string): Promise<Record<string, unknown>> {
    const corpo = await this.pedir<{ publicKey: Record<string, unknown> }>(
      '/auth/passkey/register/options',
      {
        method: 'POST',
        manterSessao: true,
        body: JSON.stringify({ slug, passcode: senha }),
      },
    );
    return corpo.publicKey;
  }

  /**
   * O desafio para entrar com uma chave já criada.
   *
   * Não leva slug nem senha: a chave é descobrível (`residentKey: required`),
   * então o aparelho mostra as que tem e a credencial que volta já diz de quem
   * é. Dizer quem é antes seria dar nome de gente a quem só chegou na porta.
   */
  async opcoesDeLogin(): Promise<Record<string, unknown>> {
    const corpo = await this.pedir<{ publicKey: Record<string, unknown> }>(
      '/auth/passkey/login/options',
      { method: 'POST', manterSessao: true, body: '{}' },
    );
    return corpo.publicKey;
  }

  /** Entra com a chave. Devolve o mesmo corpo que a senha de quatro dígitos. */
  entrarComChave(credencial: unknown): Promise<LoginResponse> {
    return this.pedir<LoginResponse>('/auth/passkey/login/verify', {
      method: 'POST',
      body: JSON.stringify({ credential: credencial }),
    });
  }

  confirmarCadastroDeChave(
    slug: string,
    senha: string,
    credencial: unknown,
    rotulo: string,
  ): Promise<{ ok: true }> {
    return this.pedir('/auth/passkey/register/verify', {
      method: 'POST',
      manterSessao: true,
      body: JSON.stringify({ slug, passcode: senha, credential: credencial, label: rotulo }),
    });
  }

  // ── Casa e ajustes ─────────────────────────────────────────────────

  casa(): Promise<Casa> {
    return this.pedir<Casa>('/casa');
  }

  /**
   * Manda algo que quem não lidera não pode gravar sozinho.
   *
   * 202 não é erro: é "virou sugestão, e quem lidera decide". `pedir` trata
   * qualquer status fora da faixa de sucesso como falha e não deixa a tela
   * distinguir os dois, então estas duas rotas vão por fora dele.
   */
  private async postarComSugestao<T>(
    caminho: string,
    corpo: unknown,
  ): Promise<{ virouSugestao: boolean; recado: string; resposta: T | null }> {
    const controle = new AbortController();
    const prazo = setTimeout(() => controle.abort(), this.timeout);
    const cabecalhos: Record<string, string> = { 'Content-Type': 'application/json' };
    const cookie = this.lerCookie?.();
    if (cookie) cabecalhos.Cookie = cookie;
    try {
      const r = await fetch(this.url(caminho), {
        method: 'POST',
        headers: cabecalhos,
        credentials: 'include',
        body: JSON.stringify(corpo),
        signal: controle.signal,
      });
      if (r.status === 401) this.aoExpirar?.();
      const resposta = await r.json().catch(() => null);
      if (!r.ok && r.status !== 202) {
        throw new ErroAPI(r.status, recadoDoErro(resposta, r.status));
      }
      return {
        virouSugestao: r.status === 202,
        recado: comoErro(resposta).message ?? '',
        // O 200 devolve o que ficou gravado, que não é sempre o que foi
        // mandado: o servidor conserta o que não faz sentido, e a tela compara
        // os dois para contar a diferença. O 202 não gravou nada ainda.
        resposta: r.ok && r.status !== 202 && resposta ? (resposta as T) : null,
      };
    } catch (err) {
      if (err instanceof ErroAPI) throw err;
      throw new ErroAPI(0, traduzir('Não foi possível salvar.'));
    } finally {
      clearTimeout(prazo);
    }
  }

  /**
   * Grava a casa. Quem não lidera recebe 202: virou sugestão, não erro. Por
   * isso o retorno diz qual dos dois aconteceu, em vez de só resolver.
   *
   * `casa` é como o servidor guardou — o `CasaApi.gravar` do Flutter devolve a
   * mesma coisa. Nulo quando virou sugestão: aí não há nada gravado para ler.
   */
  async salvarCasa(
    casa: Partial<Casa>,
  ): Promise<{ virouSugestao: boolean; recado: string; casa: Casa | null }> {
    const r = await this.postarComSugestao<Casa>('/casa', casa);
    return { virouSugestao: r.virouSugestao, recado: r.recado, casa: r.resposta };
  }

  ajustes<T = Record<string, unknown>>(): Promise<T> {
    return this.pedir<T>('/settings');
  }

  /**
   * Mesma história da casa: quem não lidera sugere, e o 202 diz isso.
   *
   * O corpo do 200 não sai daqui de propósito: quem guarda os ajustes é o
   * `usarAjustes`, e a tela chama `recarregarAjustes()` depois de gravar. Uma
   * segunda porta de entrada para o mesmo estado só criaria duas verdades.
   */
  async salvarAjustes(
    ajustes: Record<string, unknown>,
  ): Promise<{ virouSugestao: boolean; recado: string }> {
    const r = await this.postarComSugestao<unknown>('/settings', ajustes);
    return { virouSugestao: r.virouSugestao, recado: r.recado };
  }

  /**
   * Volta os ajustes ao padrão. Este não vira sugestão: desfaz o trabalho de
   * todo mundo, e o servidor responde 403 para quem não lidera.
   */
  restaurarAjustes(): Promise<unknown> {
    return this.pedir('/settings/reset', { method: 'POST', body: '{}' });
  }

  /** Aceita ou recusa uma sugestão. Só quem lidera; qualquer outro leva 403. */
  decidirSugestao(id: string, aceitar: boolean): Promise<unknown> {
    return this.pedir('/sugestoes/decidir', {
      method: 'POST',
      body: JSON.stringify({ id, aceitar }),
    });
  }

  /** O tema é a única coisa que cada um escolhe só para si. */
  salvarTema(tema: Record<string, unknown>): Promise<unknown> {
    return this.pedir('/theme', { method: 'POST', body: JSON.stringify(tema) });
  }

  /** A conta de música de quem está na sessão, para o `/u/N/` do YouTube. */
  salvarContaDeMusica(conta: string): Promise<unknown> {
    return this.pedir('/music-account', {
      method: 'POST',
      body: JSON.stringify({ account: conta }),
    });
  }

  salvarLastfm(usuario: string): Promise<unknown> {
    return this.pedir('/lastfm-account', {
      method: 'POST',
      body: JSON.stringify({ user: usuario }),
    });
  }

  // ── Contas ligadas por cookie ──────────────────────────────────────

  private async listaDeConexoes(
    caminho: string,
    corpo?: unknown,
  ): Promise<{ conexoes: Conexao[]; presets: PresetDeConexao[]; eu: string }> {
    const bruto = await this.pedir<{
      conexoes?: unknown[];
      presets?: PresetDeConexao[];
      eu?: string;
    }>(caminho, corpo === undefined ? {} : { method: 'POST', body: JSON.stringify(corpo) });
    return {
      conexoes: (bruto.conexoes ?? []).map(comoConexao),
      presets: bruto.presets ?? [],
      eu: bruto.eu ?? '',
    };
  }

  conexoes() {
    return this.listaDeConexoes('/connections');
  }

  /**
   * Cola os cookies de um site. O que vai daqui é a colagem crua, uma vez: o
   * servidor guarda e nunca devolve o valor, só quantos são e como se chamam.
   */
  salvarConexao(pedido: { preset: string; host?: string; nome?: string; cookies: string }) {
    return this.listaDeConexoes('/connections/save', pedido);
  }

  testarConexao(id: string, dono?: string) {
    return this.listaDeConexoes('/connections/check', { id, dono });
  }

  apagarConexao(id: string, dono?: string) {
    return this.listaDeConexoes('/connections/delete', { id, dono });
  }

  // ── Armazém ────────────────────────────────────────────────────────

  ler<T = Record<string, unknown>>(colecao: string): Promise<Pagina<T>> {
    return this.pedir<Pagina<T>>('/dados', { query: { colecao } });
  }

  /** `mesclar` junta com o que já está lá em vez de substituir. */
  gravar<T = Record<string, unknown>>(
    colecao: string,
    id: string,
    dados: T,
    mesclar = false,
  ): Promise<Evento<T>> {
    return this.pedir<Evento<T>>('/dados', {
      method: 'PUT',
      body: JSON.stringify({ colecao, id, dados, mesclar }),
    });
  }

  apagar(colecao: string, id: string): Promise<Evento> {
    return this.pedir<Evento>('/dados/apagar', {
      method: 'POST',
      body: JSON.stringify({ colecao, id }),
    });
  }

  /**
   * Ouve o armazém ao vivo. Devolve a função de parar.
   *
   * O `EventSource` reconecta sozinho e manda de volta o último `id` recebido,
   * então quem passou a noite fechado se acerta pedindo só o que mudou. Onde
   * ele não existe (React Native), use `ler` e o `seq` para conferir de tempos
   * em tempos — daí `temFluxo` avisa antes de tentar.
   */
  ouvir(
    aoQuadro: (quadro: QuadroDoFluxo) => void,
    aoErro?: (erro: Error) => void,
    desde?: number,
  ): () => void {
    if (!this.temFluxo()) {
      aoErro?.(new Error('Esta plataforma não tem EventSource.'));
      return () => {};
    }
    const es = new EventSource(this.url('/dados/stream', { desde }), {
      withCredentials: true,
    });
    es.onmessage = (ev: MessageEvent) => {
      try {
        aoQuadro(JSON.parse(ev.data) as QuadroDoFluxo);
      } catch {
        // Quadro quebrado não derruba o fluxo: o próximo vem inteiro.
      }
    };
    es.onerror = () => {
      // O EventSource tenta de novo sozinho; só avisamos para a tela mostrar
      // que está fora do ar. Fechar aqui mataria a reconexão de graça.
      aoErro?.(new Error('Fluxo interrompido.'));
    };
    return () => es.close();
  }

  temFluxo(): boolean {
    return typeof EventSource !== 'undefined';
  }

  // ── Cozinha ────────────────────────────────────────────────────────

  /**
   * Traz uma receita de um site. Quem busca a página é o servidor, não o
   * navegador: site de receita não manda CORS, e o guarda de SSRF que decide se
   * o endereço pode ser buscado mora lá.
   *
   * O prazo é mais largo que o padrão porque o servidor ainda vai buscar a
   * página lá fora — 15 s aqui cortaria a espera dele pela metade.
   */
  importarReceita(url: string): Promise<ReceitaImportada> {
    return this.pedir<ReceitaImportada>('/recipes/import', {
      method: 'POST',
      body: JSON.stringify({ url: url.trim() }),
      prazo: 25_000,
    });
  }

  /**
   * Sobe a foto do álbum e devolve o endereço público dela.
   *
   * Quem guarda os bytes é o servidor, e não este aparelho: o armazenamento de
   * arquivos exige a conta de serviço, que mora lá. O caminho dentro do balde
   * é montado lá também, com quem está na sessão — daqui vai só o nome.
   *
   * O prazo é largo porque são megabytes subindo, e o servidor ainda repassa a
   * foto adiante: o padrão de 15 s cortaria uma foto grande no meio.
   */
  async subirFoto(bytes: ArrayBuffer, nome: string, tipo: string): Promise<string> {
    const r = await this.pedir<{ endereco: string }>('/gallery/upload', {
      method: 'POST',
      query: { nome },
      headers: { 'Content-Type': tipo },
      body: bytes,
      prazo: 60_000,
    });
    return r.endereco;
  }

  // ── Calendários ────────────────────────────────────────────────────
  //
  // O `PartnerCalendarService` do Flutter, linha por linha. Tudo passa pelo
  // nosso servidor e não pelo dono do calendário: os endereços secretos de
  // .ics não mandam CORS nenhum, e é justamente passar por aqui que mantém o
  // link fora do navegador. O que volta nunca traz o endereço — o
  // `publicShape` de `server/src/calendars.js` corta o campo antes de
  // responder, e por isso não há onde guardá-lo deste lado.
  //
  // O prazo é o `_timeout` de 25 s de lá: o servidor tem 20 s para buscar o
  // .ics lá fora, e o padrão de 15 s cortaria a chamada antes da resposta.

  async calendarios(): Promise<ListasDeCalendarios> {
    const r = await this.pedir<unknown>('/calendars', { prazo: 25_000 });
    return listasDeCalendariosDeMapa(r);
  }

  /**
   * Cria ou edita um dos seus. Sem `id` é novo; com `id` é edição.
   *
   * `url` vazio é omitido de propósito, e não mandado vazio: na edição é assim
   * que se diz "mantenha o link que já está aí". Mandar `''` apagaria o feed.
   */
  async salvarCalendario(dados: {
    id?: string;
    nome: string;
    assinatura: boolean;
    url?: string;
    cor?: string;
    compartilhado?: boolean;
  }): Promise<void> {
    const url = (dados.url ?? '').trim();
    await this.pedir('/calendars/save', {
      method: 'POST',
      body: JSON.stringify({
        ...(dados.id ? { id: dados.id } : {}),
        name: dados.nome,
        kind: dados.assinatura ? 'ics' : 'local',
        ...(url ? { url } : {}),
        color: dados.cor ?? '',
        shared: dados.compartilhado ?? false,
      }),
      prazo: 25_000,
    });
  }

  async compartilharCalendario(id: string, compartilhado: boolean): Promise<void> {
    await this.pedir('/calendars/share', {
      method: 'POST',
      body: JSON.stringify({ id, shared: compartilhado }),
      prazo: 25_000,
    });
  }

  async apagarCalendario(id: string): Promise<void> {
    await this.pedir('/calendars/delete', {
      method: 'POST',
      body: JSON.stringify({ id }),
      prazo: 25_000,
    });
  }

  /**
   * Os eventos de um calendário. Vazio para quem não tem feed: esses eventos
   * moram no armazém da casa, e quem os mostra é a tela do calendário.
   */
  async eventosDoCalendario(id: string): Promise<EventoDeCalendario[]> {
    const r = await this.pedir<unknown>('/calendars/events', { query: { id }, prazo: 25_000 });
    return eventosDeCalendarioDeMapa(r);
  }

  // ── Livros ─────────────────────────────────────────────────────────

  /**
   * Busca o livro inteiro em texto puro, tentando os endereços na ordem.
   *
   * Quem busca é o servidor: o Gutenberg não manda CORS nenhum, e a lista de
   * quem pode ser buscado (só `gutenberg.org` e `archive.org`) mora lá, do lado
   * onde ela vale de verdade.
   *
   * O prazo é mais largo que o padrão porque o servidor tem 20 s para cada
   * origem lá fora — 15 s aqui derrubaria a chamada antes da resposta dele.
   */
  textoDeLivro(urls: string[]): Promise<TextoDeLivro> {
    return this.pedir<TextoDeLivro>('/books/text', {
      method: 'POST',
      body: JSON.stringify({ urls }),
      prazo: 25_000,
    });
  }

  // ── Alexa ──────────────────────────────────────────────────────────
  //
  // A conta da Amazon é da casa: ler as listas é de todo mundo, conectar e
  // desconectar são do líder. O cookie fica no servidor e não volta em
  // resposta nenhuma, então não há nada dele para guardar aqui.

  alexaStatus(): Promise<AlexaStatus> {
    return this.pedir<AlexaStatus>('/alexa/status');
  }

  /** As listas já importadas, que os dois leem. */
  async alexaCompartilhadas(): Promise<ListaAlexa[]> {
    const r = await this.pedir<{ lists?: ListaAlexa[] }>('/alexa/shared');
    return r?.lists ?? [];
  }

  /** As listas que estão na conta da Amazon agora. Pode falhar com 409/502. */
  async alexaDisponiveis(): Promise<ListaAlexa[]> {
    const r = await this.pedir<{ lists?: ListaAlexa[] }>('/alexa/lists');
    return r?.lists ?? [];
  }

  alexaConectar(cookie: string, host = ''): Promise<unknown> {
    return this.pedir('/alexa/connect', {
      method: 'POST',
      body: JSON.stringify(host ? { cookie, host } : { cookie }),
    });
  }

  alexaDesconectar(): Promise<unknown> {
    return this.pedir('/alexa/disconnect', { method: 'POST', body: JSON.stringify({}) });
  }

  /** Traz uma lista para a cópia compartilhada. Devolve o conjunto já novo. */
  async alexaImportar(listId: string): Promise<ListaAlexa[]> {
    const r = await this.pedir<{ lists?: ListaAlexa[] }>('/alexa/import', {
      method: 'POST',
      body: JSON.stringify({ listId }),
    });
    return r?.lists ?? [];
  }

  /**
   * Manda itens para uma lista já importada. Item repetido é descartado no
   * servidor, então apertar duas vezes não faz a Echo ler o mercado duas vezes.
   */
  alexaMandar(
    listId: string,
    itens: string[],
  ): Promise<{ adicionados: number; jaEstavam: number; parcial?: boolean }> {
    return this.pedir('/alexa/push', {
      method: 'POST',
      body: JSON.stringify({ listId, itens }),
    });
  }

  /** Risca (ou desrisca) um item na Alexa, para a Echo parar de lê-lo. */
  async alexaRiscar(listId: string, itemId: string, done = true): Promise<ListaAlexa[]> {
    const r = await this.pedir<{ lists?: ListaAlexa[] }>('/alexa/complete', {
      method: 'POST',
      body: JSON.stringify({ listId, itemId, done }),
    });
    return r?.lists ?? [];
  }

  /** Tira a lista da cópia compartilhada. Não mexe na conta da Amazon. */
  async alexaEsquecer(listId: string): Promise<ListaAlexa[]> {
    const r = await this.pedir<{ lists?: ListaAlexa[] }>('/alexa/remove', {
      method: 'POST',
      body: JSON.stringify({ listId }),
    });
    return r?.lists ?? [];
  }

  /**
   * Toca o celular da outra pessoa. O tipo é um dos que `notifications.js`
   * conhece; um desconhecido volta 400 e nada é enviado.
   *
   * Falha calada de propósito: quem chama já fez o que importava (gravou a
   * sala, mandou a carta), e um aviso perdido não pode desfazer isso.
   */
  async avisar(tipo: string, previa = ''): Promise<void> {
    try {
      await this.pedir('/push/notify', {
        method: 'POST',
        body: JSON.stringify({ type: tipo, body: previa }),
      });
    } catch {
      /* melhor esforço */
    }
  }

  /**
   * A metade pública do par VAPID. 503 quando o servidor ainda não tem par.
   *
   * `manterSessao` em todas as rotas de aviso: um 401 aqui é a sessão vencida
   * numa aba esquecida aberta, e derrubar o app inteiro por causa da tela de
   * notificações seria trocar um recado por uma expulsão.
   */
  async chaveDeAviso(): Promise<string> {
    const r = await this.pedir<{ publicKey?: string }>('/push/key', { manterSessao: true });
    return r?.publicKey ?? '';
  }

  /** Se o servidor sabe enviar, e em quantos aparelhos você já recebe. */
  async avisosLigados(): Promise<{ enabled: boolean; devices: number }> {
    const r = await this.pedir<{ enabled?: boolean; devices?: number }>('/push/status', {
      manterSessao: true,
    });
    return { enabled: r?.enabled === true, devices: r?.devices ?? 0 };
  }

  /** Guarda a inscrição deste navegador. O endereço é o do serviço de push. */
  async assinarAvisos(endereco: string): Promise<number> {
    const r = await this.pedir<{ devices?: number }>('/push/subscribe', {
      method: 'POST',
      body: JSON.stringify({ endpoint: endereco }),
      manterSessao: true,
    });
    return r?.devices ?? 0;
  }

  /** Esquece a inscrição deste navegador. */
  async desassinarAvisos(endereco: string): Promise<void> {
    await this.pedir('/push/unsubscribe', {
      method: 'POST',
      body: JSON.stringify({ endpoint: endereco }),
      manterSessao: true,
    });
  }

  /** Manda um aviso para você mesmo, só para provar que funciona. */
  async avisoDeTeste(): Promise<boolean> {
    try {
      await this.pedir('/push/test', { method: 'POST', body: '{}', manterSessao: true });
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Os servidores de gelo da chamada de voz da sessão.
   *
   * O repasse existe porque a NAT das operadoras daqui costuma deixar a
   * chamada conectar sem nunca entregar som nem imagem. Quando não há repasse
   * configurado o servidor devolve uma lista vazia em vez de erro, e a chamada
   * segue só com os STUN públicos — que é onde ela já funcionava. Por isso
   * qualquer tropeço aqui também vira lista vazia, e não exceção.
   */
  async turn(): Promise<ServidorDeGelo[]> {
    try {
      const r = await this.pedir<{ iceServers?: ServidorDeGelo[] }>('/watch/turn', { prazo: 6_000 });
      return Array.isArray(r?.iceServers) ? r.iceServers : [];
    } catch {
      return [];
    }
  }

  // ── A conta no Sicredi ─────────────────────────────────────────────
  //
  // Só leitura, e só para quem lidera: a credencial do Sicredi é pessoal e
  // registrada no nome de uma pessoa só. As três rotas de `financas_rotas.js`
  // respondem 401 para quem não é, e 503 quando falta o certificado no
  // servidor — que é bem diferente de a conversa com o Sicredi ter dado
  // errado, e por isso a tela precisa dos dois separados.
  //
  // O prazo é maior que o padrão porque o nosso servidor ainda vai falar com o
  // Sicredi, por mTLS, antes de responder.

  /** Se a conta está ligada aqui, e se o que ela mostra é de verdade. */
  async financasStatus(): Promise<{ pronto: boolean; producao: boolean }> {
    const r = await this.pedir<{ pronto?: boolean; ambiente?: string }>('/financas/status', {
      prazo: 30_000,
    });
    return { pronto: r?.pronto === true, producao: r?.ambiente === 'producao' };
  }

  async financasSaldo(): Promise<Saldo> {
    return saldoDeJson(await this.pedir<unknown>('/financas/saldo', { prazo: 30_000 }));
  }

  async financasExtrato(de: Date, ate: Date, pagina = 0): Promise<Extrato> {
    return extratoDeJson(
      await this.pedir<unknown>('/financas/extrato', {
        query: {
          dataInicio: diaIso(de),
          dataFim: diaIso(ate),
          page: pagina,
          size: POR_PAGINA,
        },
        prazo: 30_000,
      }),
    );
  }

  // ── TV ao vivo ─────────────────────────────────────────────────────
  //
  // A assinatura é de quem lidera, e o serviço dela não deixa outra origem
  // entrar. Quem guarda a credencial é o servidor; daqui só saem pedidos de
  // leitura e um endereço para tocar. Toda rota pode responder 409: é o
  // servidor dizendo que ainda não há conexão, e a tela volta ao pareamento.
  //
  // O prazo é maior que o padrão porque o nosso servidor ainda vai falar com o
  // serviço lá fora, e ele mesmo espera 20 s antes de desistir.

  /** Se há conexão agora, por qual caminho, e se a chave foi recusada. */
  async iptvLigacao(): Promise<LigacaoIptv> {
    const r = await this.pedir<{ paired?: boolean; via?: string | null; recusada?: boolean }>(
      '/iptv/status',
      { prazo: 25_000 },
    );
    return { ligada: r?.paired === true, via: r?.via ?? '', recusada: r?.recusada === true };
  }

  /**
   * Guarda a chave da conta do serviço. Só o líder; campo vazio apaga a chave.
   *
   * Devolve `null` quando deu certo e o recado quando não. A chave nunca volta
   * do servidor: guardar é de mão única de propósito.
   */
  async iptvGuardarChave(chave: string): Promise<string | null> {
    try {
      await this.pedir('/iptv/key', {
        method: 'POST',
        body: JSON.stringify({ chave: chave.trim() }),
        prazo: 25_000,
      });
      return null;
    } catch (err) {
      if (err instanceof ErroAPI) return err.message;
      return 'Não consegui falar com o servidor agora.';
    }
  }

  /** Pede um código para aprovar na conta do serviço. */
  async iptvComecarPareamento(): Promise<PareamentoIptv | null> {
    const r = await this.pedir<{ code?: string; deviceToken?: string; connectUrl?: string }>(
      '/iptv/pair/start',
      { method: 'POST', body: JSON.stringify({}), prazo: 25_000 },
    );
    const codigo = `${r?.code ?? ''}`;
    const aparelho = `${r?.deviceToken ?? ''}`;
    if (!codigo || !aparelho) return null;
    /* O `connectUrl` é do serviço, não nosso, e é ele que vira `href` no site e
       `Linking.openURL` no celular. O que não for endereço de navegador vira
       vazio, e a tela então mostra só o código. */
    return { codigo, aparelho, endereco: enderecoDeFora(`${r?.connectUrl ?? ''}`, '') };
  }

  /**
   * Pergunta se já aprovaram. Qualquer tropeço vira `pending`: a tela só reage
   * a `approved`, e um erro de rede no meio da espera não é motivo de alarme.
   */
  async iptvVerPareamento(aparelho: string): Promise<string> {
    try {
      const r = await this.pedir<{ status?: string }>('/iptv/pair/poll', {
        method: 'POST',
        body: JSON.stringify({ deviceToken: aparelho }),
        prazo: 25_000,
      });
      return `${r?.status ?? 'pending'}`;
    } catch {
      return 'pending';
    }
  }

  private async iptvLer(query: Record<string, string | number | undefined>): Promise<unknown> {
    return this.pedir<unknown>('/iptv/read', { query, prazo: 25_000 });
  }

  /** As listas da conta: as próprias e as que compartilharam com ela. */
  async iptvListas(): Promise<ListaIptv[]> {
    return listasIptv(await this.iptvLer({ path: 'playlists' }));
  }

  /** As categorias de um tipo: `live`, `vod` ou `series`. */
  async iptvCategorias(lista: string, tipo: string): Promise<EntradaIptv[]> {
    const corpo = await this.iptvLer({ path: 'xtream/categories', playlist_id: lista, type: tipo });
    return itensIptv(corpo).map(entradaIptv).filter((e): e is EntradaIptv => e !== null);
  }

  /** O que existe dentro de uma categoria. */
  async iptvCanais(
    lista: string,
    tipo: string,
    categoria: string,
    pagina = 1,
    limite = 120,
  ): Promise<EntradaIptv[]> {
    const corpo = await this.iptvLer({
      path: 'xtream/streams',
      playlist_id: lista,
      type: tipo,
      category_id: categoria,
      page: pagina,
      limit: limite,
    });
    return itensIptv(corpo).map(entradaIptv).filter((e): e is EntradaIptv => e !== null);
  }

  /** Procura no catálogo inteiro, não só na categoria aberta. */
  async iptvProcurar(
    lista: string,
    tipo: string,
    termo: string,
    limite = 120,
  ): Promise<EntradaIptv[]> {
    const corpo = await this.iptvLer({
      path: 'xtream/search',
      playlist_id: lista,
      type: tipo,
      q: termo,
      limit: limite,
    });
    return itensIptv(corpo).map(entradaIptv).filter((e): e is EntradaIptv => e !== null);
  }

  /** Os episódios de uma série, já achatados na ordem das temporadas. */
  async iptvEpisodios(lista: string, serie: string): Promise<EntradaIptv[]> {
    const corpo = await this.iptvLer({
      path: 'xtream/series_info',
      playlist_id: lista,
      series_id: serie,
    });
    return episodiosIptv(corpo);
  }

  /**
   * O endereço que toca, já embrulhado no nosso proxy.
   *
   * O serviço assina um link do proxy dele, mas esse proxy não manda CORS e
   * não reescreve os pedaços do HLS — o `<video>` não consegue buscar nenhum
   * dos dois. Passando por `/watch/stream`, os dois problemas somem.
   */
  async iptvFluxo(
    lista: string,
    tipo: string,
    id: string,
    ext = '',
  ): Promise<FluxoIptv | null> {
    const r = await this.pedir<{ url?: string; hls?: boolean }>('/iptv/stream-url', {
      query: {
        playlist_id: lista,
        type: tipo,
        stream_id: id,
        ext: ext || undefined,
      },
      prazo: 25_000,
    });
    const assinado = `${r?.url ?? ''}`;
    if (!assinado) return null;
    return { url: this.url('/watch/stream', { url: assinado }), hls: r?.hls === true };
  }

  // ── Torrent ────────────────────────────────────────────────────────

  /**
   * O endereço que toca um arquivo de dentro de um torrent.
   *
   * Procurar torrent não passa por aqui: o Torrentio responde com CORS aberto
   * e o aparelho fala direto com ele. Só o tocar precisa do servidor, porque
   * a caixa que serve o vídeo exige uma assinatura que só ele sabe fazer.
   *
   * `null` quando o servidor recusa — sem sessão, ou reprodução por torrent
   * não configurada nesta instalação — para a tela poder dizer isso em vez de
   * mandar o tocador abrir um endereço que vai dar 403.
   */
  async torrenteEndereco(hash: string, arquivo: number): Promise<string | null> {
    try {
      const r = await this.pedir<{ url?: string }>('/cine/torrent-url', {
        query: { hash, fileIdx: arquivo },
        prazo: 10_000,
      });
      const url = `${r?.url ?? ''}`;
      return url || null;
    } catch {
      return null;
    }
  }

  // ── Kovu ───────────────────────────────────────────────────────────

  /**
   * Uma resposta inteira, de uma vez. É o caminho de quem não tem leitura de
   * corpo em pedaços — React Native, por exemplo.
   *
   * O `idioma` entra aqui dentro, e não em cada `extra` de quem chama, porque
   * é a única forma de ninguém esquecer: a escolha de língua mora no aparelho,
   * o servidor não tem como adivinhá-la, e um caminho que esquecesse de mandá-la
   * faria o Kovu voltar ao português no meio de um app em inglês. Vai depois do
   * `extra` de propósito — não é ajuste de chamada, é o estado do app.
   */
  async kovu(
    messages: Array<{ role: string; content: string }>,
    extra: Record<string, unknown> = {},
  ): Promise<string> {
    const r = await this.pedir<{ reply: string }>('/ai', {
      method: 'POST',
      body: JSON.stringify({ ...extra, messages, idioma: idioma() }),
    });
    return r.reply;
  }

  /**
   * A resposta em pedaços, conforme chega. `POST` com corpo, então não dá para
   * usar `EventSource` aqui: é `fetch` lendo o corpo. Devolve a função de
   * cancelar. Onde `response.body` não existe, o corpo inteiro é lido de uma vez
   * e passa pelo mesmo leitor de quadros: a fala chega de um golpe só, mas chega
   * inteira, com as ferramentas e os recados de espera.
   *
   * Os quadros são os de `stream()` em `server/src/ai.js`: `{content}` é o que
   * a pessoa lê, `{tool_status}` é o "estou procurando" enquanto ela espera,
   * `{error}` é falha no meio da resposta e `{reasoning}` é o modelo pensando
   * alto — este último fica de fora de propósito, para não virar texto.
   */
  kovuEmPedacos(
    messages: Array<{ role: string; content: string }>,
    aoPedaco: (texto: string) => void,
    aoFim?: () => void,
    aoErro?: (erro: Error) => void,
    extra: Record<string, unknown> = {},
    aoStatus?: (recado: string) => void,
  ): () => void {
    const controle = new AbortController();
    // Quem abortou muda o que a tela mostra: o cancelamento vem de quem saiu da
    // conversa e não merece recado nenhum, o estouro de prazo é falha e precisa
    // aparecer. Os dois chegam como o mesmo `AbortError`, então a diferença só
    // existe se for anotada aqui.
    let estourou = false;

    (async () => {
      try {
        const cabecalhos: Record<string, string> = {
          'Content-Type': 'application/json',
          Accept: 'text/event-stream',
        };
        const cookie = this.lerCookie?.();
        if (cookie) cabecalhos.Cookie = cookie;

        const prazo = setTimeout(
          () => {
            estourou = true;
            controle.abort();
          },
          emPedacos() ? PRAZO_CABECALHO : PRAZO_RESPOSTA,
        );
        let r: Response;
        try {
          r = await fetch(this.url('/ai'), {
            method: 'POST',
            headers: cabecalhos,
            credentials: 'include',
            body: JSON.stringify({ ...extra, messages, stream: true, idioma: idioma() }),
            signal: controle.signal,
          });
        } finally {
          // Onde se lê em pedaços, aqui: o cabeçalho chegou, e o resto é o Kovu
          // falando. Onde não se lê, este `await` já trouxe a resposta inteira,
          // e o prazo que acabou de ser desarmado era o dela.
          clearTimeout(prazo);
        }
        if (r.status === 401) this.aoExpirar?.();
        if (!r.ok) {
          const corpo = await r.json().catch(() => null);
          throw new ErroAPI(r.status, traduzir(comoErro(corpo).message ?? 'O Kovu não respondeu.'));
        }

        /**
         * Lê os quadros inteiros que houver no texto e devolve o pedaço final
         * que ainda não fechou. Os dois caminhos passam por aqui: o que lê em
         * pedaços guarda a sobra para a volta seguinte, o que recebe tudo de uma
         * vez chama uma vez só.
         */
        const consumir = (texto: string): string => {
          // Eventos são separados por linha em branco; o resto fica para a
          // próxima volta em vez de virar JSON pela metade.
          const partes = texto.split('\n\n');
          const resto = partes.pop() ?? '';
          for (const parte of partes) {
            for (const linha of parte.split('\n')) {
              if (!linha.startsWith('data:')) continue;
              const bruto = linha.slice(5).trim();
              if (!bruto || bruto === '[DONE]') continue;
              try {
                const q = JSON.parse(bruto) as {
                  content?: string;
                  tool_status?: string;
                  error?: string;
                };
                // O texto do Kovu já vem na língua da casa — é o prompt que
                // decide. Estes dois não: são frases escritas no servidor, e é
                // aqui que elas viram palavra do app.
                if (q.error) throw new ErroAPI(502, traduzir(q.error));
                if (q.content) aoPedaco(q.content);
                else if (q.tool_status) aoStatus?.(traduzir(q.tool_status));
              } catch (err) {
                // O erro do próprio Kovu sobe; o resto é ruído de proxy, e um
                // evento que não é JSON não pode derrubar a resposta inteira.
                if (err instanceof ErroAPI) throw err;
              }
            }
          }
          return resto;
        };

        if (!r.body) {
          // O corpo já está todo aqui, e é o mesmo texto de sempre. Pedir de
          // novo sem `stream` gastava uma segunda conversa com o modelo e, pior,
          // caía no caminho que não tem ferramenta nenhuma: era esse o Kovu que
          // no celular respondia sem ir olhar, ou não respondia. O `\n\n` no fim
          // fecha o último quadro caso o servidor tenha sido cortado sem ele.
          consumir(`${await r.text()}\n\n`);
          aoFim?.();
          return;
        }

        const leitor = r.body.getReader();
        const dec = new TextDecoder();
        let sobra = '';
        for (;;) {
          const { done, value } = await leitor.read();
          if (done) break;
          sobra = consumir(sobra + dec.decode(value, { stream: true }));
        }
        aoFim?.();
      } catch (err) {
        if ((err as Error)?.name === 'AbortError') {
          // Sem `estourou`, o prazo morreria calado: esta mesma linha é a que
          // engole o cancelamento de quem fechou a tela.
          if (estourou) {
            aoErro?.(new ErroAPI(0, traduzir('O servidor demorou demais para responder.')));
          }
          return;
        }
        aoErro?.(err instanceof Error ? err : new Error(traduzir('O Kovu falhou.')));
      }
    })();

    return () => controle.abort();
  }
}

export type { Documento, Evento, Pagina, QuadroDoFluxo };
